/**
 * Estado del menú lateral (colapsado + ancho elegido por el usuario) y su serialización a la
 * cookie compartida `sidebar_vibia`.
 *
 * ⚠️ COPIA CONSCIENTE: espejo literal de
 * `services/vibiafront/professional-area/src/components/sidebar/sidebar-layout.ts` (la cookie la
 * comparten ambos frontales durante el strangler; professional-area no puede importar código de
 * aquí — mismo caso que su `lib/project-name-validation.ts`). Si cambias el formato de la cookie,
 * cambia AMBOS lados: los dos tests comparten la tabla COOKIE_FIXTURES para pillar divergencias.
 *
 * El MÁXIMO del ancho NO vive aquí: lo impone el CSS del shell (240px, el ancho de diseño).
 * Aquí solo hay un tope de cordura para no guardar basura en la cookie.
 */

/** Nombre de la cookie compartida con professional-area. */
export const SIDEBAR_COOKIE = 'sidebar_vibia'

/** Ancho del rail de iconos (modo colapsado). */
export const RAIL_WIDTH = 64

/** Por debajo de esto las labels no se leen: es el mínimo del modo expandido. */
export const MIN_EXPANDED_WIDTH = 180

/** Arrastrando por debajo de este ancho, el menú se pliega al rail. */
export const SNAP_WIDTH = 140

/** Tope de cordura de la cookie. El máximo real, más estrecho, lo aplica el CSS del shell. */
export const MAX_STORED_WIDTH = 640

/** Paso del teclado sobre el grip (con Shift, ×4). */
export const KEYBOARD_STEP = 16

/** Ancho de DISEÑO del sidebar del eproject (la columna fija histórica) — techo del resize. */
export const DESIGN_WIDTH = 240

export const DEFAULT_SIDEBAR_LAYOUT = { collapsed: false, width: null }

const COOKIE_PATTERN = /^(expanded|collapsed)(?::(\d{2,4}))?$/

// decodeURIComponent revienta con secuencias % mal formadas; una cookie manipulada no debe tirar el shell.
const safeDecode = (raw) => {
	try {
		return decodeURIComponent(raw)
	} catch (e) {
		return raw
	}
}

/** Encierra un ancho arrastrado en el rango almacenable. */
export const clampWidth = (px) => {
	if (!Number.isFinite(px)) return MIN_EXPANDED_WIDTH
	return Math.round(Math.min(MAX_STORED_WIDTH, Math.max(MIN_EXPANDED_WIDTH, px)))
}

/**
 * Lee el valor de la cookie. Cualquier cosa que no encaje (ausente, basura, ancho fuera de rango)
 * cae al default: el menú expandido con el ancho de diseño.
 */
export const parseSidebarCookie = (raw) => {
	if (!raw) return DEFAULT_SIDEBAR_LAYOUT
	const match = COOKIE_PATTERN.exec(safeDecode(raw))
	if (!match) return DEFAULT_SIDEBAR_LAYOUT
	const width = match[2] ? clampWidth(Number(match[2])) : null
	return { collapsed: match[1] === 'collapsed', width }
}

export const serializeSidebarCookie = (layout) => {
	const state = layout.collapsed ? 'collapsed' : 'expanded'
	// El ancho se guarda también colapsado: al expandir se recupera el que el usuario había dejado.
	return layout.width == null ? state : `${state}:${clampWidth(layout.width)}`
}

/**
 * Traduce un ancho arrastrado (px crudos, sin clampar) al estado resultante. Por debajo de
 * `SNAP_WIDTH` el menú se pliega al rail conservando el ancho previo, para poder restaurarlo.
 */
export const nextStateFromDrag = (px, current) => {
	if (px < SNAP_WIDTH) return { collapsed: true, width: current.width }
	return { collapsed: false, width: clampWidth(px) }
}

/**
 * Ancho EFECTIVO en px para el CSS del eproject. A diferencia de professional-area (ancho de
 * diseño proporcional → el techo lo pone un clamp() en CSS), aquí el diseño es fijo (240px) y
 * el JS puede resolver el valor final directamente.
 */
export const effectiveWidthPx = (layout) => {
	if (layout.collapsed) return RAIL_WIDTH
	if (layout.width == null) return DESIGN_WIDTH
	return Math.min(clampWidth(layout.width), DESIGN_WIDTH)
}
